/**
 * Shared utilities — Safety Rail Source
 */

/**
 * Check if user prefers reduced motion.
 * @returns {boolean}
 */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Initialize scroll-reveal animations via IntersectionObserver.
 * Elements with `.reveal` class get `.reveal--visible` when they enter viewport.
 */
function initScrollReveal() {
  var reveals = document.querySelectorAll('.reveal');
  if (!reveals.length) return;

  if (prefersReducedMotion()) {
    reveals.forEach(function (el) {
      el.classList.add('reveal--visible');
    });
    return;
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('reveal--visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
  );

  reveals.forEach(function (el) {
    observer.observe(el);
  });
}

/**
 * Force the "e-" prefix in product titles to render lowercase even when the
 * global title style uses `text-transform: capitalize`.
 *
 * Example: "NextGen TranzVolt e-Hoist" stored in Shopify renders as
 * "NextGen TranzVolt E-Hoist" because CSS capitalize uppercases the first
 * letter of every word. This wraps just the "e-" with a span that has
 * `text-transform: lowercase` so it stays as "e-Hoist".
 *
 * Targets all common product-title selectors across the theme.
 */
function initEPrefixCasing() {
  var selectors = [
    '.product-info__title',
    '.product-sticky-bar__title',
    '.product-fbt__title',
    '.rp-card__title',
    '.col-product-card__title',
    '.product-card__title',
    '.fc-product-card__title',
    '.search-product-card__title',
    '.sc-card__title',
    '.bc-current'
  ];
  var els = document.querySelectorAll(selectors.join(', '));
  if (!els.length) return;

  /* Match a word starting with "e-" or "E-" followed by any letter.
     Examples matched: "e-Hoist", "E-Bike", "e-Commerce".
     Word boundary (\b) ensures we don't match mid-word. */
  var pattern = /\b[eE]-(?=[A-Za-z])/g;

  els.forEach(function (el) {
    /* Only walk text nodes — preserves any existing markup like <sup>® */
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach(function (textNode) {
      var text = textNode.nodeValue;
      if (!pattern.test(text)) return;
      pattern.lastIndex = 0;

      var frag = document.createDocumentFragment();
      var lastIndex = 0;
      var match;
      while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
          frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
        }
        var span = document.createElement('span');
        span.style.textTransform = 'lowercase';
        span.textContent = match[0]; /* "e-" or "E-" */
        frag.appendChild(span);
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < text.length) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex)));
      }
      textNode.parentNode.replaceChild(frag, textNode);
    });
  });
}

/**
 * Lazy-load images that have a `data-src` attribute.
 * Uses IntersectionObserver with 200px rootMargin for early loading.
 */
function initLazyLoad() {
  var lazyImages = document.querySelectorAll('img[data-src]');
  if (!lazyImages.length) return;

  var imageObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var img = entry.target;
          img.src = img.getAttribute('data-src');
          img.removeAttribute('data-src');
          imageObserver.unobserve(img);
        }
      });
    },
    { rootMargin: '200px 0px' }
  );

  lazyImages.forEach(function (img) {
    imageObserver.observe(img);
  });
}

/**
 * Style + superscript Searchanise search-results page.
 *
 * Searchanise (3rd-party Shopify Search & Filter app) renders
 * everything on the search page via its own JS — product cards,
 * collection results, pagination, filter sidebars — and re-renders
 * them on every keystroke, filter toggle, sort, and page change.
 * That means any DOM edits we make get blown away unless we
 * re-apply them whenever Searchanise rewrites the page.
 *
 * This function:
 *   1. Watches <body> for ANY Searchanise element to appear,
 *      then adds `data-snize-results="true"` to <body> + class
 *      `snize-results-active` to the Shopify section wrapper —
 *      giving CSS reliable hooks.
 *   2. Wraps ® / ™ in <sup> inside product, collection, page,
 *      and category titles (any "snize-*-title" / .snize-name).
 *   3. Re-runs on every body mutation (childList + subtree +
 *      characterData), debounced via requestAnimationFrame.
 *      processMarks() is idempotent — text nodes already inside
 *      a <sup> are skipped, so repeated runs are safe and cheap.
 */
function initSearchResultsPage() {
  if (!('MutationObserver' in window)) return;

  var activated = false;

  /* ── Step A: add CSS hooks to ancestor elements ── */
  function activatePageStyles(snizeEl) {
    if (activated) return;
    activated = true;

    document.body.setAttribute('data-snize-results', 'true');

    /* Walk up to the Shopify section wrapper and inner .section div */
    var shopifySection = snizeEl.closest(
      '.shopify-section-page, .shopify-section, [class*="shopify-section"]'
    );
    if (shopifySection) shopifySection.classList.add('snize-results-active');

    var sectionDiv = snizeEl.closest('.section');
    if (sectionDiv) sectionDiv.classList.add('snize-results-active');
  }

  /* ── Step B: wrap ® ™ in <sup> ──
     Idempotent: skips text nodes already inside a <sup>, so it's safe
     to run on every mutation. Covers product titles (.snize-title),
     collection/category/page titles, and any "snize-*-title" variant
     Searchanise may use for non-product results. */
  function processMarks(root) {
    var els = root.querySelectorAll(
      '.snize-title, .snize-collection-title, .snize-page-title, ' +
      '.snize-category-title, .snize-name, ' +
      '[class*="snize-"][class*="-title"]'
    );
    if (!els.length) return;

    els.forEach(function (el) {
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      var nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);

      nodes.forEach(function (textNode) {
        /* Skip text nodes already inside a <sup> — prevents re-wrapping
           on subsequent MutationObserver callbacks. */
        if (textNode.parentNode && textNode.parentNode.nodeName === 'SUP') return;

        var text = textNode.nodeValue;
        if (text.indexOf('\u00AE') === -1 && text.indexOf('\u2122') === -1) return;

        var frag = document.createDocumentFragment();
        var parts = text.split(/(\u00AE|\u2122)/);
        parts.forEach(function (part) {
          if (part === '\u00AE' || part === '\u2122') {
            var sup = document.createElement('sup');
            sup.textContent = part;
            frag.appendChild(sup);
          } else if (part) {
            frag.appendChild(document.createTextNode(part));
          }
        });
        textNode.parentNode.replaceChild(frag, textNode);
      });
    });
  }

  /* ── Step C: find any Searchanise root to activate page styles ── */
  function findSnizeRoot() {
    return document.querySelector(
      '#snize_results, .snize-search-results, #snize-search-results-main, ' +
      '[class*="snize-search-results"], li.snize-product'
    );
  }

  /* ── Step D: one-shot sweep + re-run on every DOM change ──
     Searchanise re-renders its results on every keystroke, filter,
     sort, and pagination — and sometimes replaces its container
     entirely. A body-level observer (childList + subtree +
     characterData) guarantees we catch every one of those mutations.
     processMarks() is idempotent, and the rAF debounce coalesces
     bursts of Searchanise DOM writes into a single pass. */
  function runAll() {
    if (!activated) {
      var snizeEl = findSnizeRoot();
      if (snizeEl) activatePageStyles(snizeEl);
    }
    processMarks(document.body);
  }

  runAll();

  var queued = false;
  function queueRun() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      runAll();
    });
  }

  var bodyObserver = new MutationObserver(queueRun);
  bodyObserver.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true
  });
}
